(() => {
  const normalize = (value = '') =>
    String(value)
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');

  let timer = null;

  function getProductRoot() {
    return (
      document.querySelector('product-info') ||
      document.querySelector('main')
    );
  }

  function getGallery(root) {
    return (
      root.querySelector('media-gallery') ||
      document.querySelector('media-gallery')
    );
  }

  /*
    Read ALL selected Shopify option values.

    Examples:

    Bracelet:
    Black
    Silver
    Silver 925

    Masbaha:
    Brass
    Round

    Necklace:
    Yellow Gold
    Silver 925
  */
  function getSelectedValues(root) {
    const values = [];

    root.querySelectorAll('select').forEach((select) => {
      const value = normalize(select.value);

      if (value) {
        values.push(value);
      }
    });

    root
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((input) => {
        const value = normalize(input.value);

        if (value) {
          values.push(value);
        }
      });

    return [...new Set(values)];
  }

  function getAlt(element) {
    const img =
      element.matches?.('img')
        ? element
        : element.querySelector('img');

    return normalize(img?.getAttribute('alt') || '');
  }

  function setVisible(element, visible) {
    if (!element) return;

    element.hidden = !visible;

    if (visible) {
      element.style.removeProperty('display');
    } else {
      element.style.setProperty(
        'display',
        'none',
        'important'
      );
    }
  }

  function getMediaItems(gallery) {
    let items = [
      ...gallery.querySelectorAll(
        '.f-product__media, .product__media-item'
      )
    ];

    if (!items.length) {
      items = [
        ...gallery.querySelectorAll('[data-media-id]')
      ].filter((item) => {
        return (
          item.querySelector('img') &&
          !item.closest(
            '[id^="GalleryThumbnailItem"], .f-product__media-thumbnail'
          )
        );
      });
    }

    return [...new Set(items)];
  }

  /*
    Build possible tags from the selected values.

    Example:

    Selected:
    Black
    Silver
    Silver 925

    Possible:
    variant-black-silver-silver-925
    variant-black-silver
    variant-black-silver-925
    variant-silver-silver-925
    variant-black
    variant-silver
    variant-silver-925

    We always try the most specific matches first.
  */
  function buildCandidates(values) {
    const candidates = [];

    function combinations(size, start, current) {
      if (current.length === size) {
        candidates.push({
          values: [...current],
          tag: `variant-${current.join('-')}`,
          specificity: size
        });

        return;
      }

      for (
        let i = start;
        i < values.length;
        i++
      ) {
        current.push(values[i]);

        combinations(
          size,
          i + 1,
          current
        );

        current.pop();
      }
    }

    const maxSize =
      Math.min(3, values.length);

    for (
      let size = maxSize;
      size >= 1;
      size--
    ) {
      combinations(size, 0, []);
    }

    return candidates;
  }

  function restoreNormalGallery(gallery) {
    getMediaItems(gallery).forEach((item) => {
      setVisible(item, true);
    });

    gallery
      .querySelectorAll(
        '[id^="GalleryThumbnailItem"], .f-product__media-thumbnail'
      )
      .forEach((thumb) => {
        setVisible(thumb, true);
      });

    window.dispatchEvent(
      new Event('resize')
    );
  }

  function filterGallery(focusExact = false) {
    const root =
      getProductRoot();

    if (!root) return;

    const gallery =
      getGallery(root);

    if (!gallery) return;

    const mediaItems =
      getMediaItems(gallery);

    if (!mediaItems.length) return;

    /*
      Find every variant tag currently
      present on this product.
    */
    const variantAlts =
      mediaItems
        .map(getAlt)
        .filter((alt) =>
          alt.startsWith('variant-')
        );

    /*
      SAFETY #1

      Product contains NO variant tags?

      Do absolutely nothing.
    */
    if (!variantAlts.length) {
      return;
    }

    /*
      Universal/shared images must NOT
      activate filtering by themselves.
    */
    const specificAlts =
      new Set(
        variantAlts.filter(
          (alt) =>
            alt !== 'variant-all' &&
            !alt.endsWith('-all')
        )
      );

    /*
      If product contains only variant-all,
      leave its normal gallery alone.
    */
    if (!specificAlts.size) {
      restoreNormalGallery(gallery);
      return;
    }

    const selectedValues =
      getSelectedValues(root);

    if (!selectedValues.length) {
      restoreNormalGallery(gallery);
      return;
    }

    const candidates =
      buildCandidates(selectedValues);

    /*
      Find the most specific selected combination
      that actually exists in the product's Alt Text.
    */
    const match =
      candidates.find((candidate) =>
        specificAlts.has(candidate.tag)
      );

    /*
      SAFETY #2

      Product uses variant tags, but none match
      the customer's current selection?

      Show normal gallery instead of blanking it.
    */
    if (!match) {
      restoreNormalGallery(gallery);
      return;
    }

    const exactTag = match.tag;

    /*
      The first value in the successful match
      is considered the main visual grouping.

      Example:

      Black + Silver
      exactTag  = variant-black-silver
      sharedTag = variant-black-all

      Round
      exactTag  = variant-round
      sharedTag = variant-round-all
    */
    const sharedTag =
      `variant-${match.values[0]}-all`;

    const universalTag =
      'variant-all';

    let exactMedia = null;

    mediaItems.forEach((item) => {
      const alt = getAlt(item);

      const show =
        alt === exactTag ||
        alt === sharedTag ||
        alt === universalTag;

      setVisible(item, show);

      if (
        alt === exactTag &&
        !exactMedia
      ) {
        exactMedia = item;
      }
    });

    /*
      Laura thumbnails
    */
    const thumbnails = [
      ...gallery.querySelectorAll(
        '[id^="GalleryThumbnailItem"], .f-product__media-thumbnail'
      )
    ];

    thumbnails.forEach((thumbnail) => {
      const alt =
        getAlt(thumbnail);

      const show =
        alt === exactTag ||
        alt === sharedTag ||
        alt === universalTag;

      setVisible(
        thumbnail,
        show
      );
    });

    window.dispatchEvent(
      new Event('resize')
    );

    /*
      Focus the exact selected variant image
      when Laura supports it.
    */
    if (
      focusExact &&
      exactMedia &&
      typeof gallery.setActiveMedia === 'function'
    ) {
      const mediaId =
        exactMedia.dataset.mediaId;

      if (mediaId) {
        setTimeout(() => {
          try {
            gallery.setActiveMedia(mediaId);
          } catch (e) {}
        }, 100);
      }
    }
  }

  function schedule(focus = false) {
    clearTimeout(timer);

    timer = setTimeout(() => {
      filterGallery(focus);
    }, 120);
  }

  function init() {
    /*
      Initial load
    */
    schedule(false);

    /*
      Customer changes Shopify variant
    */
    document.addEventListener(
      'change',
      () => {
        schedule(true);
      },
      true
    );

    /*
      Laura may rebuild the product gallery
      when a variant changes.
    */
    const root =
      getProductRoot();

    if (root) {
      const observer =
        new MutationObserver(() => {
          schedule(false);
        });

      observer.observe(root, {
        childList: true,
        subtree: true
      });
    }
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  /*
    Shopify Theme Editor
  */
  document.addEventListener(
    'shopify:section:load',
    () => {
      schedule(false);
    }
  );
})();